import { pageFormatsMatch } from './types';
import { COPY } from './presentationCopy';
import { LibrarySlideGroup, LibrarySlideUpdate, PresentationLibraryLabel, PresentationLibrarySlide, PresentationSlide } from './types';

export function libraryUpdateHeading(update: Pick<LibrarySlideUpdate, 'index' | 'libraryName'>): string {
  const page = `Sida ${update.index + 1}`;
  return update.libraryName ? `${page} · ${update.libraryName}` : page;
}

export function libraryUpdateDetail(update: Pick<LibrarySlideUpdate, 'backgroundChanged' | 'objectsChanged' | 'sourceMissing'>): string {
  if (update.sourceMissing) return COPY.libraryUpdateSourceMissing;
  if (update.backgroundChanged && update.objectsChanged) return COPY.libraryUpdateContentAndBackground;
  if (update.backgroundChanged) return COPY.libraryUpdateBackground;
  return COPY.libraryUpdateContent;
}

export function isLinkedLibrarySlide(
  slide?: { librarySlideId?: string | null; libraryDetached?: boolean } | null,
): boolean {
  return Boolean(slide?.librarySlideId) && !slide?.libraryDetached;
}

export function resolveLibrarySlideLabelIds(
  slide: { labelIds?: string[] | null; group?: string | null },
  labels: Array<Pick<PresentationLibraryLabel, '_id' | 'legacyKey'>>,
): string[] {
  const assigned = (slide.labelIds || []).filter(Boolean);
  if (assigned.length) return assigned;
  if (!slide.group) return [];
  return labels.filter((label) => label.legacyKey === slide.group).map((label) => label._id);
}

export function librarySlideLabelNames(
  slide: { labelIds?: string[] | null; group?: string | null },
  labels: Array<Pick<PresentationLibraryLabel, '_id' | 'name' | 'legacyKey'>>,
): string[] {
  return resolveLibrarySlideLabelIds(slide, labels)
    .map((id) => labels.find((label) => label._id === id)?.name)
    .filter((name): name is string => Boolean(name));
}

export function filterLibrarySlidesByLabel<T extends { labelIds?: string[] | null; group?: string | null }>(
  items: T[],
  labelId: string,
  labels: Array<Pick<PresentationLibraryLabel, '_id' | 'legacyKey'>>,
): T[] {
  if (!labelId) return items;
  return items.filter((item) => resolveLibrarySlideLabelIds(item, labels).includes(labelId));
}

export function splitLibrarySlidesByArchive<T extends { isActive?: boolean }>(items: T[]) {
  return {
    active: items.filter((item) => item.isActive !== false),
    archived: items.filter((item) => item.isActive === false),
  };
}

export function toggleLibrarySlideLabel(
  slide: { labelIds?: string[] | null; group?: string | null },
  labelId: string,
  labels: Array<Pick<PresentationLibraryLabel, '_id' | 'legacyKey'>>,
): string[] {
  const current = resolveLibrarySlideLabelIds(slide, labels);
  return current.includes(labelId)
    ? current.filter((id) => id !== labelId)
    : [...current, labelId];
}

export function filterLibrarySlides<T extends { name: string; group?: string | null; labelIds?: string[] | null }>(
  items: T[],
  query: string,
  labels: Array<Pick<PresentationLibraryLabel, '_id' | 'name' | 'legacyKey'>> = [],
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return items;
  return items.filter((item) => {
    const name = String(item.name || '').toLowerCase();
    const group = String(item.group || '').toLowerCase();
    const labelText = librarySlideLabelNames(item, labels).join(' ').toLowerCase();
    return name.includes(needle) || group.includes(needle) || labelText.includes(needle);
  });
}

export function librarySlidesMatchingPageFormat<T extends { pageFormat?: string | null }>(
  items: T[],
  pageFormat?: string | null,
): T[] {
  return items.filter((item) => pageFormatsMatch(item.pageFormat, pageFormat));
}

export function libraryPreviewSlide(item: PresentationLibrarySlide): PresentationSlide {
  return {
    slideId: item._id,
    layoutId: 'library',
    order: 0,
    objects: item.objects || [],
    backgroundColor: item.backgroundColor || '#FFFFFF',
  };
}

export function libraryGroupLabel(group?: LibrarySlideGroup | string | null): string {
  if (group === 'product') return COPY.libraryGroupProduct;
  if (group === 'price') return COPY.libraryGroupPrice;
  if (group === 'agenda') return COPY.libraryGroupAgenda;
  if (group === 'other') return COPY.libraryGroupOther;
  return '';
}

export function librarySourceMissing(
  slide: { librarySlideId?: string | null; libraryDetached?: boolean } | null | undefined,
  sources: Array<{ _id: string; isActive?: boolean }>,
): boolean {
  if (!slide?.librarySlideId) return false;
  const source = sources.find((item) => item._id === slide.librarySlideId);
  return !source || source.isActive === false;
}
